/**
 * **The track a ship steams**, and the reason it is a shape rather than a list
 * of waypoints.
 *
 * A patrol here is a **racetrack**: two straights joined by two 180° turns.
 * Position and heading are closed-form functions of distance run, and distance
 * run is `speed × time` — so a sloop's pose is a pure function of the encounter
 * clock, with no accumulator, no integration error and nothing to serialize.
 * That is the same trick `airship-pass.ts`'s `airshipRoundsAtTick` uses on a
 * rate of fire, applied to a position.
 *
 * ## Why not a polyline, which is what a patrol obviously is
 *
 * Because a polyline has corners, and a corner is where the two things this lane
 * must guarantee come apart.
 *
 * A ship on a polyline either **snaps** her heading at each vertex — which reads
 * as a toy boat and is exactly the "fighter-plane manoeuvre" the brief rules out
 * — or she **lags** it behind the path, which means she crabs sideways for the
 * two hundred metres it takes a sloop to come round. Rounding the corner fixes
 * both, and once the corner is a curve of a stated radius the honest thing is to
 * make the whole track out of curves and straights, so that *the curve the census
 * tests is the curve the ship runs*. There is no smoothing step between the
 * proof and the motion, because there is nothing left to smooth.
 *
 * It also makes the safety proof finite and total. `patrolSamples` walks the
 * closed curve at a fixed spacing, and `ship-deployment.ts` refuses a track any
 * of whose samples is not in open water with a margin. A polyline plus a
 * separate turn model would have needed the proof to cover a family of paths the
 * ship *might* take; a racetrack is one path, and every metre of it is tested.
 *
 * ## The turn is eased at both ends, and that is the 2026-09-01 correction
 *
 * The first version of this file made each end a **half-circle of the ship's
 * hard-over turning radius**, which is two faults in one curve and the user saw
 * both of them as "her turns look too tight":
 *
 * 1. **The radius was a hard-rudder figure.** A collision-avoidance turn is not
 *    what an escort does when she reaches the end of her leg.
 *    `SHIP_CRUISE_TURN_RADIUS_M` is the cruising one — twice the radius, because
 *    steady turning radius goes as the reciprocal of rudder angle.
 * 2. **The curvature stepped.** At the end of a straight it went from zero to
 *    `1/R` between one tick and the next, which is a rudder slammed hard over
 *    with no travel time and no hull response. Real helm takes seconds and a
 *    real hull answers it over about a ship length.
 *
 * So a turn here is a **spiral in, a steady arc, and a spiral out**: curvature
 * ramps linearly from 0 to `1/R` over `SHIP_TURN_EASE_M` of track, holds, and
 * ramps back. That makes each ramp a clothoid — the transition curve a railway
 * or a road uses between a straight and a curve, for exactly this reason — and
 * the middle a circular arc of the stated radius.
 *
 * ### Keeping it closed-form
 *
 * A clothoid's *heading* is closed-form (`θ = s²/2ER`) and its *position* is a
 * Fresnel integral, which is not elementary. Three things keep the promise at
 * the top of this file intact anyway:
 *
 * - **The quadrature is a pure function of `s`.** `clothoidOffset` runs a fixed
 *   32-interval Simpson rule from the start of the spiral to the point asked
 *   for. It is a sum, but it is not an *accumulator*: nothing is carried between
 *   ticks, two peers evaluating the same `s` get the same bits, and a replay
 *   resumed at any tick lands where the original run had her.
 * - **Only the spirals are quadratured.** The steady arc between them is the
 *   ordinary closed-form circle, started from the spiral's own endpoint, so the
 *   long middle of every turn is exact and continuous with the ramp by
 *   construction.
 * - **The second half of a turn is the first half mirrored**, using the exact
 *   symmetry of a curve whose heading satisfies `θ(L − s) = π − θ(s)`: the
 *   advance along the entry heading comes back to `U(L − s) = U(s)` and the
 *   lateral offset to `V(L − s) = V(L) − V(s)`. So the turn's *exit* is the
 *   entry displaced by exactly `V(L)` abeam and zero ahead — an identity, not a
 *   number that quadrature error can spoil — and the circuit closes to the bit.
 *
 * ## The sense of the circuit
 *
 * She turns to **starboard** at both ends — a right-hand circuit. Nothing
 * decides that but the need to decide it; a left-hand one is the same curve
 * traversed the other way, and offering both would double the state without
 * changing a picture. What *is* varied per ship is `axisRad`, the straights'
 * length, and `phaseM`, which is where on the circuit she starts.
 *
 * ## The frame
 *
 * Everything is in the **recentred world frame the sim and the scene share** —
 * metres, `x` east, `z` south, `y` unused because the model's origin is on its
 * own waterline (ship-design.md §9.2) and the sea is the datum. Headings follow
 * the port's own convention: heading 0 flies toward −z (`math3d.ts`'s
 * `BODY_FORWARD`), and a positive heading swings toward +x.
 */

import { SHIP_TURN_EASE_M } from "./ship-class.ts";

/** A closed racetrack circuit on the sea surface. Immutable; `ship-deployment.ts` draws it once and nothing edits it. */
export interface ShipPatrol {
  /** Centre of the circuit, metres. */
  readonly centreX: number;
  readonly centreZ: number;
  /** Heading of the outbound straight, radians. */
  readonly axisRad: number;
  /** Length of each straight, metres. */
  readonly straightM: number;
  /**
   * Radius of the **steady** part of each turn, metres — the tightest the
   * circuit ever gets, reached only in the middle of a turn once the helm is
   * fully over. Never below `SHIP_HARD_TURN_RADIUS_M`, and in practice
   * `SHIP_CRUISE_TURN_RADIUS_M`; `ship-deployment.ts` is what sets it.
   *
   * **It is not half the distance between the straights.** The spirals at
   * either end push them further apart than that — see `turnLateralOffsetM`,
   * which is the number the circuit is actually laid out on.
   */
  readonly radiusM: number;
  /** Metres already run when the encounter opens: which point of the circuit she is at on tick 0. */
  readonly phaseM: number;
}

/** Where a ship is and which way she is pointing. */
export interface ShipTrackPoint {
  readonly x: number;
  readonly z: number;
  readonly headingRad: number;
}

/** An offset in a turn's entry frame: `ahead` along the entry heading, `abeam` to starboard of it. */
interface TurnOffset {
  readonly ahead: number;
  readonly abeam: number;
}

/**
 * Intervals in the Simpson rule over a spiral. Even, and fixed — a step count
 * that depended on the length asked for would make two evaluations of the same
 * curve at different resolutions, which is how a closed loop stops closing.
 */
const SPIRAL_STEPS = 32;

/** The unit vector of a heading, in the world's `x`/`z` plane. */
function headingVector(headingRad: number): { readonly x: number; readonly z: number } {
  return { x: Math.sin(headingRad), z: -Math.cos(headingRad) };
}

/**
 * The spiral length actually used for a turn of `radiusM`.
 *
 * `SHIP_TURN_EASE_M` is a property of the *ship* — how far she runs while the
 * helm goes over — and a turn has to be long enough to contain two of them.
 * Clamped at `π·radiusM`, which is the point at which the two spirals meet and
 * the steady arc vanishes; nothing this port deploys comes near it (the cruise
 * radius leaves a ramp a fifth of the arc), and the clamp is here so a caller
 * that hands in an absurdly tight radius gets a curve rather than a turn that
 * over-rotates.
 */
function easeLengthM(radiusM: number): number {
  return Math.min(SHIP_TURN_EASE_M, Math.PI * radiusM);
}

/** Total track in one 180° turn: the steady arc `πR` plus one spiral's worth of extra length. */
export function turnLengthM(radiusM: number): number {
  return Math.PI * radiusM + easeLengthM(radiusM);
}

/** How far through the turn she has swung after `s` metres of it — closed form in all three segments. */
function turnHeadingRad(radiusM: number, s: number): number {
  const ease = easeLengthM(radiusM);
  const length = turnLengthM(radiusM);
  if (ease <= 0) return s / radiusM;
  if (s <= ease) return (s * s) / (2 * ease * radiusM);
  if (s >= length - ease) return Math.PI - turnHeadingRad(radiusM, length - s);
  return ease / (2 * radiusM) + (s - ease) / radiusM;
}

/**
 * Curvature at `s` metres into a turn, radians per metre — the ramp, the steady
 * `1/R`, and the ramp back down. Positive: every turn on this circuit is to
 * starboard.
 */
function turnCurvature(radiusM: number, s: number): number {
  const ease = easeLengthM(radiusM);
  const length = turnLengthM(radiusM);
  if (ease <= 0) return 1 / radiusM;
  if (s <= ease) return s / (ease * radiusM);
  if (s >= length - ease) return Math.max(0, length - s) / (ease * radiusM);
  return 1 / radiusM;
}

/**
 * The offset from a spiral's start to `s` metres along it, `s ≤ ease`, by
 * Simpson's rule on `∫cos θ` and `∫sin θ`.
 *
 * Pure in `s`: no state, no memo, no dependence on the order evaluations are
 * asked for in.
 */
function clothoidOffset(radiusM: number, s: number): TurnOffset {
  const step = s / SPIRAL_STEPS;
  let ahead = 0;
  let abeam = 0;
  for (let index = 0; index <= SPIRAL_STEPS; index += 1) {
    const weight = index === 0 || index === SPIRAL_STEPS ? 1 : index % 2 === 1 ? 4 : 2;
    const angle = turnHeadingRad(radiusM, index * step);
    ahead += weight * Math.cos(angle);
    abeam += weight * Math.sin(angle);
  }
  return { ahead: (ahead * step) / 3, abeam: (abeam * step) / 3 };
}

/**
 * The offset from a turn's entry to `s` metres into it, in the entry frame,
 * for `s` in the turn's **first half**.
 *
 * The spiral is quadratured; the steady arc is the exact circle, laid on from
 * the spiral's own endpoint, so there is no seam between them.
 */
function turnOffsetFirstHalf(radiusM: number, s: number): TurnOffset {
  const ease = easeLengthM(radiusM);
  if (ease <= 0) {
    const plain = s / radiusM;
    return { ahead: radiusM * Math.sin(plain), abeam: radiusM * (1 - Math.cos(plain)) };
  }
  if (s <= ease) return clothoidOffset(radiusM, s);
  const spiral = clothoidOffset(radiusM, ease);
  const entryAngle = turnHeadingRad(radiusM, ease);
  const angle = turnHeadingRad(radiusM, s);
  return {
    ahead: spiral.ahead + radiusM * (Math.sin(angle) - Math.sin(entryAngle)),
    abeam: spiral.abeam + radiusM * (Math.cos(entryAngle) - Math.cos(angle)),
  };
}

/**
 * How far a 180° turn displaces the ship **abeam** of her entry heading — the
 * distance between the two straights of a racetrack, and the number the census
 * needs sea room for.
 *
 * `2 × radiusM` for a plain half-circle; more than that here, because the
 * spirals spend track without turning. Twice the half-way offset, which makes
 * the exit an identity rather than a quadrature result.
 */
export function turnLateralOffsetM(radiusM: number): number {
  const remembered = lateralOffsets.get(radiusM);
  if (remembered !== undefined) return remembered;
  const offset = 2 * turnOffsetFirstHalf(radiusM, turnLengthM(radiusM) / 2).abeam;
  lateralOffsets.set(radiusM, offset);
  return offset;
}

/**
 * Answers already worked out, by radius.
 *
 * A memo of a pure function of one number, and it is here for a measured
 * reason: every pose inside a turn needs this offset, `patrolSamples` takes
 * hundreds of them per candidate track and the census takes twenty thousand,
 * and each one is otherwise a fresh 32-interval quadrature. It cannot change an
 * answer — the same radius has the same offset for ever — and in practice the
 * map holds exactly one entry, because every ship this port deploys turns at
 * `SHIP_CRUISE_TURN_RADIUS_M`.
 */
const lateralOffsets = new Map<number, number>();

/**
 * The offset from a turn's entry to `s` metres into it, anywhere in the turn.
 *
 * Past the half-way point it is the first half mirrored: `U(L − s) = U(s)` and
 * `V(L − s) = V(L) − V(s)`, both exact consequences of `θ(L − s) = π − θ(s)`.
 * So the turn ends at exactly `(0, V(L))` — no advance at all along the entry
 * heading — and the racetrack closes without anything having to be nudged.
 */
function turnOffset(radiusM: number, s: number): TurnOffset {
  const length = turnLengthM(radiusM);
  if (s <= length / 2) return turnOffsetFirstHalf(radiusM, s);
  const mirrored = turnOffsetFirstHalf(radiusM, length - s);
  return { ahead: mirrored.ahead, abeam: turnLateralOffsetM(radiusM) - mirrored.abeam };
}

/** Perimeter of the circuit: two straights and two eased 180° turns. */
export function patrolLengthM(patrol: ShipPatrol): number {
  return 2 * patrol.straightM + 2 * turnLengthM(patrol.radiusM);
}

/**
 * One end's turn, as a point and a heading.
 *
 * `entryX`/`entryZ` is where she crosses from the straight into the turn and
 * `entryHeadingRad` the heading she held along it. She turns to **starboard**,
 * so the offset's `abeam` component is laid off to starboard of that heading and
 * the heading itself is the entry plus however far she has swung — which is the
 * tangent of the curve by construction rather than by adjustment, the property
 * `combat-ships.test.ts` measures as "no crabbing, anywhere".
 */
function turnPoint(
  entryX: number,
  entryZ: number,
  entryHeadingRad: number,
  radiusM: number,
  alongM: number,
): ShipTrackPoint {
  const forward = headingVector(entryHeadingRad);
  const starboard = headingVector(entryHeadingRad + Math.PI / 2);
  const offset = turnOffset(radiusM, alongM);
  return {
    x: entryX + forward.x * offset.ahead + starboard.x * offset.abeam,
    z: entryZ + forward.z * offset.ahead + starboard.z * offset.abeam,
    headingRad: entryHeadingRad + turnHeadingRad(radiusM, alongM),
  };
}

/**
 * Where she is after running `travelledM` from the start of the circuit,
 * wrapping as many times round as the distance calls for.
 *
 * The four legs in order: the outbound straight, the turn at its far end, the
 * inbound straight, the turn back onto the start.
 */
export function patrolAt(patrol: ShipPatrol, travelledM: number): ShipTrackPoint {
  const perimeter = patrolLengthM(patrol);
  const turn = turnLengthM(patrol.radiusM);
  const along = ((travelledM % perimeter) + perimeter) % perimeter;

  const axis = headingVector(patrol.axisRad);
  const beam = headingVector(patrol.axisRad + Math.PI / 2);
  const half = patrol.straightM / 2;
  // The two straights are `turnLateralOffsetM` apart, so each runs half of that
  // off the centreline — the outbound one to *port* and the inbound one to
  // starboard, which is what leaves room for the two starboard turns between
  // them. For a plain half-circle this would be the radius; the eased turn is
  // wider, and this is the one place that difference has to be spent.
  const halfWidth = turnLateralOffsetM(patrol.radiusM) / 2;
  const startX = patrol.centreX - axis.x * half - beam.x * halfWidth;
  const startZ = patrol.centreZ - axis.z * half - beam.z * halfWidth;

  if (along < patrol.straightM) {
    return { x: startX + axis.x * along, z: startZ + axis.z * along, headingRad: patrol.axisRad };
  }
  if (along < patrol.straightM + turn) {
    return turnPoint(
      startX + axis.x * patrol.straightM,
      startZ + axis.z * patrol.straightM,
      patrol.axisRad,
      patrol.radiusM,
      along - patrol.straightM,
    );
  }
  const backX = patrol.centreX + axis.x * half + beam.x * halfWidth;
  const backZ = patrol.centreZ + axis.z * half + beam.z * halfWidth;
  if (along < 2 * patrol.straightM + turn) {
    const run = along - patrol.straightM - turn;
    return { x: backX - axis.x * run, z: backZ - axis.z * run, headingRad: patrol.axisRad + Math.PI };
  }
  return turnPoint(
    backX - axis.x * patrol.straightM,
    backZ - axis.z * patrol.straightM,
    patrol.axisRad + Math.PI,
    patrol.radiusM,
    along - 2 * patrol.straightM - turn,
  );
}

/**
 * How sharply she is turning after `travelledM`, radians of heading per metre
 * of track — zero on the straights, `1/radiusM` in the middle of a turn, and
 * ramping between over the spirals. Positive everywhere, because every turn on
 * this circuit is to starboard.
 *
 * Exported for the *picture*: `apps/web/src/flight/scene-ships.ts` multiplies it
 * by her speed to heel her outward in a turn, and `ship-wake.ts` uses it for
 * nothing at all — the wake reads her poses. It changes no hashed state, and a
 * consumer that wants a rate of turn should multiply by the speed it stepped
 * her at rather than assume one.
 */
export function patrolCurvatureAt(patrol: ShipPatrol, travelledM: number): number {
  const perimeter = patrolLengthM(patrol);
  const turn = turnLengthM(patrol.radiusM);
  const along = ((travelledM % perimeter) + perimeter) % perimeter;
  if (along < patrol.straightM) return 0;
  if (along < patrol.straightM + turn) return turnCurvature(patrol.radiusM, along - patrol.straightM);
  if (along < 2 * patrol.straightM + turn) return 0;
  return turnCurvature(patrol.radiusM, along - 2 * patrol.straightM - turn);
}

/**
 * Her pose at `timeS` seconds into the encounter, at a constant `speedMps`.
 *
 * This is the whole of the ship's motion model. Two peers that agree on the
 * clock agree on the ship to the last bit without exchanging anything, and a
 * replay resumed from any tick puts her exactly where the original run had her.
 */
export function patrolPoseAt(patrol: ShipPatrol, timeS: number, speedMps: number): ShipTrackPoint {
  return patrolAt(patrol, patrol.phaseM + speedMps * timeS);
}

/** How sharply she is turning at `timeS`, radians per metre. Visual only — see `patrolCurvatureAt`. */
export function patrolCurvatureAtTime(patrol: ShipPatrol, timeS: number, speedMps: number): number {
  return patrolCurvatureAt(patrol, patrol.phaseM + speedMps * timeS);
}

/**
 * Every point of the circuit at no more than `spacingM` apart, starting at the
 * circuit's own origin and going right round.
 *
 * **This is the census's curve.** The count is taken from the perimeter with
 * `Math.ceil`, so the real spacing is at or under the one asked for and the
 * last sample is one step short of closing the loop rather than a duplicate of
 * the first.
 */
export function patrolSamples(patrol: ShipPatrol, spacingM: number): readonly ShipTrackPoint[] {
  const perimeter = patrolLengthM(patrol);
  const count = Math.max(1, Math.ceil(perimeter / Math.max(spacingM, 1)));
  const step = perimeter / count;
  return Array.from({ length: count }, (_unused, index) => patrolAt(patrol, index * step));
}
